import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from "react";

import { useUser } from "@/contexts";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { chatService } from "@/services";
import { useSound } from "@/hooks";

export interface Notification {
  id: string;
  type: string;
  message: string;
  read: boolean;
  createdAt: Date | string;
  [key: string]: any;
}

interface NotificationContextProps {
  notifications: Notification[];
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  clearNotifications: () => void;
  setActiveChatId: (id: string | null) => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined,
);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);

  const { userData } = useUser();

  const playNotification = useSound("/sounds/notification.mp3");

  const previousUnreadRef = useRef<number>(0);

  const [conversations] = useCollectionData(
    userData?.uid ? chatService.getMyConversationsQuery(userData.uid) : null,
  );

  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (!conversations || !userData?.uid) return;

    const uid = userData.uid;

    // Calculamos o total de mensagens não lidas
    const unread = conversations.reduce((acc: number, conv: any) => {
      // REGRA: Só somamos se o ID da conversa NÃO for o que está aberto agora
      if (conv.id === activeChatId) return acc;
      return acc + (conv.unreadCount?.[uid] ?? 0);
    }, 0);

    // Se for a primeira vez que recebemos os dados (isFirstLoad),
    // apenas sincronizamos o contador sem tocar o som.
    if (isFirstLoad.current) {
      previousUnreadRef.current = unread;
      isFirstLoad.current = false;
      return; // Sai sem tocar o som
    }

    // Toca o som apenas se o número global aumentar nas próximas atualizações
    if (unread > previousUnreadRef.current) {
      playNotification();
    }

    previousUnreadRef.current = unread;
  }, [conversations, activeChatId, userData?.uid]);

  const addNotification = useCallback((notification: Notification) => {
    setNotifications((prev) => [notification, ...prev]);
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        markAsRead,
        clearNotifications,
        setActiveChatId,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }

  return context;
};
